/**
 * Campaigns — every Open and Backlog role in the hiring plan, with its owner and this recruiter's
 * outreach counts. "To review" and "Screening" are gone (Oct 19 2026): the Review step no longer
 * exists, and Screening never had a data source. "Replied" counts the replies recorded on the cards.
 */
import { useMemo } from 'react';
import { cn } from '../../../../shared';
import type { Campaign } from '../../types/taOutreach';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { Badge } from './outreachUi';

/** SelectItem needs a non-empty value: this stands for "no filter". */
const ALL = '__all__';

export type Scope = 'mine' | 'all';
export type StatusFilter = 'all' | 'Open' | 'Backlog';

/** "sofia.struempfler@finn.com" → matches an owner named "Sofia Strümpfler" (umlauts folded). */
function isMine(owner: string | null, email: string) {
  if (!owner) return false;
  const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ü/g, 'ue');
  const [first, last] = email.split('@')[0].split('.');
  const o = fold(owner);
  return Boolean(first && o.includes(first)) && (!last || o.includes(last.replace(/ue/g, 'u')) || o.includes(last));
}

const Filter = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      'h-8 rounded-md border border-solid px-[13px] text-[13px]',
      active ? 'border-[#1A1A1A] bg-[#1A1A1A] font-semibold text-white' : 'border-[#E9EAEC] bg-white font-medium text-[#707070] hover:border-[#979797]'
    )}
  >
    {children}
  </button>
);

function statusBadge(c: Campaign) {
  if (!c.postingId) return <Badge tone="grey">No Lever posting</Badge>;
  if (c.counts.drafts || c.counts.ready) return <Badge tone="blue">Drafts waiting</Badge>;
  // Accepted on the shortlist before Oct 19 2026 and never queued: "Write their InMails" in Write & send.
  if (c.counts.toReview) return <Badge tone="amber">Accepted, no InMail yet</Badge>;
  if (c.counts.contacted) return <Badge tone="green">Outreach running</Badge>;
  return <Badge tone="grey">Not sourcing</Badge>;
}

export function CampaignsList({
  campaigns,
  loading,
  error,
  myEmail,
  scope,
  status,
  q,
  dept,
  owner,
  onFilter,
  onOpen,
}: {
  campaigns: Campaign[];
  loading: boolean;
  error: string | null;
  myEmail: string;
  /** The filters live in the URL (OutreachTab), so leaving the page and coming back keeps them. */
  scope: Scope;
  status: StatusFilter;
  q: string;
  /** Department and TA owner filters (Sofia, Oct 19 2026); '' = all, owner '-' = unassigned. */
  dept: string;
  owner: string;
  onFilter: (patch: { scope?: string | null; status?: string | null; q?: string | null; dept?: string | null; owner?: string | null }) => void;
  onOpen: (c: Campaign) => void;
}) {
  const setScope = (v: Scope) => onFilter({ scope: v === 'all' ? 'all' : null });
  const setStatus = (v: StatusFilter) => onFilter({ status: v === 'all' ? null : v });
  const setQ = (v: string) => onFilter({ q: v || null });
  const mineCount = campaigns.filter((c) => isMine(c.owner, myEmail)).length;
  const sorted = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => Boolean(x)))].sort((a, b) => a.localeCompare(b, 'de'));
  const departments = sorted(campaigns.map((c) => c.department));
  const owners = sorted(campaigns.map((c) => c.owner));
  const rows = useMemo(
    () =>
      campaigns.filter(
        (c) =>
          (scope === 'all' || isMine(c.owner, myEmail)) &&
          (status === 'all' || c.status === status) &&
          (!q || c.title.toLowerCase().includes(q.toLowerCase())) &&
          (!dept || c.department === dept) &&
          (!owner || (owner === '-' ? !c.owner : c.owner === owner))
      ),
    [campaigns, scope, status, q, dept, owner, myEmail]
  );

  return (
    <div>
      <p className="mb-[18px] text-[13px] text-[#707070]">
        Roles come from the hiring database (Open and Backlog). Open one to build its brief and source candidates.
      </p>
      <div className="mb-[18px] flex flex-wrap items-center gap-[7px]">
        <Filter active={scope === 'mine'} onClick={() => setScope('mine')}>
          My roles · {mineCount}
        </Filter>
        <Filter active={scope === 'all'} onClick={() => setScope('all')}>
          All roles · {campaigns.length}
        </Filter>
        <span className="mx-1 h-5 w-px bg-[#E9EAEC]" />
        {(['all', 'Open', 'Backlog'] as StatusFilter[]).map((s) => (
          <Filter key={s} active={status === s} onClick={() => setStatus(s)}>
            {s === 'all' ? 'Any status' : `${s} · ${campaigns.filter((c) => c.status === s).length}`}
          </Filter>
        ))}
        <span className="mx-1 h-5 w-px bg-[#E9EAEC]" />
        <Select value={dept || ALL} onValueChange={(v) => onFilter({ dept: v === ALL ? null : v })}>
          <SelectTrigger aria-label="Department" className={cn('h-8 w-auto min-w-[150px] text-[13px]', dept ? 'font-semibold text-[#1A1A1A]' : 'text-[#707070]')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any department</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d} value={d}>
                {d} · {campaigns.filter((c) => c.department === d).length}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={owner || ALL} onValueChange={(v) => onFilter({ owner: v === ALL ? null : v })}>
          <SelectTrigger aria-label="TA manager" className={cn('h-8 w-auto min-w-[150px] text-[13px]', owner ? 'font-semibold text-[#1A1A1A]' : 'text-[#707070]')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any TA manager</SelectItem>
            {owners.map((o) => (
              <SelectItem key={o} value={o}>
                {o} · {campaigns.filter((c) => c.owner === o).length}
              </SelectItem>
            ))}
            <SelectItem value="-">Unassigned · {campaigns.filter((c) => !c.owner).length}</SelectItem>
          </SelectContent>
        </Select>
        <input
          type="search"
          placeholder="Search roles"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto h-8 rounded-md border border-solid border-[#E9EAEC] px-[11px] text-[13px]"
        />
      </div>
      {error && <p className="text-[13px] text-[#E71D3F]">{error}</p>}
      {loading && <p className="text-[13px] text-[#707070]">Reading the hiring plan…</p>}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="text-left text-xs font-semibold text-[#707070]">
                {['Role', 'Status', 'Owner', 'Drafts', 'Contacted', 'Replied', ''].map((h) => (
                  <th key={h} className="whitespace-nowrap border-b border-solid border-[#E9EAEC] pb-2 pr-3.5 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.postingId || c.title} className="text-[13px]">
                  <td className="border-b border-solid border-[#F3F3F3] py-3.5 pr-3.5">
                    <button type="button" disabled={!c.postingId} onClick={() => onOpen(c)} className="text-left text-sm font-semibold text-[#1A1A1A] hover:text-[#0072EA] disabled:hover:text-[#1A1A1A]">
                      {c.title}
                    </button>
                    <span className="block text-xs text-[#707070]">
                      {[c.department, c.status === 'Backlog' ? 'Backlog' : null, c.openings > 1 ? `${c.openings} openings` : null].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  <td className="border-b border-solid border-[#F3F3F3] pr-3.5">{statusBadge(c)}</td>
                  <td className="border-b border-solid border-[#F3F3F3] pr-3.5">{c.owner || <span className="text-[#979797]">Unassigned</span>}</td>
                  {[c.counts.drafts + c.counts.ready, c.counts.contacted, c.replied].map((n, i) => (
                    <td key={i} className="border-b border-solid border-[#F3F3F3] pr-3.5 [font-variant-numeric:tabular-nums]">
                      {n || <span className="text-[#979797]">0</span>}
                    </td>
                  ))}
                  <td className="border-b border-solid border-[#F3F3F3] text-right">
                    {c.postingId && (
                      <button type="button" onClick={() => onOpen(c)} className="h-8 rounded-md border border-solid border-[#1A1A1A] bg-white px-3.5 text-xs font-semibold hover:bg-[#1A1A1A] hover:text-white">
                        {c.counts.toReview + c.counts.drafts + c.counts.ready + c.counts.contacted ? 'Open' : 'Start sourcing'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <p className="mt-3 text-[13px] text-[#707070]">No roles match.</p>}
        </div>
      )}
    </div>
  );
}
